import { mergeRefs } from "@regrello/core-utils";
import React, { useCallback } from "react";
import { type FieldPath, type FieldValues, useController } from "react-hook-form";
import { RegrelloFormFieldSelect } from "../RegrelloFormFieldSelect";
import type { RegrelloControlledFormFieldSelectProps } from "./types";
import { getErrorProp, useInvokeOnChangeOnRequiredStateChange } from "./utils";

/**
 * A controlled select field for use with react-hook-form.
 * The caller is responsible for loading the options and providing them into this component.
 */
function RegrelloControlledFormFieldSelectInternal<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue,
>({
  controllerProps,
  onValueChange,
  ...selectFieldProps
}: RegrelloControlledFormFieldSelectProps<TFieldValues, TName, TValue>) {
  const { field, fieldState } = useController(controllerProps);

  // Separate out the `ref` so we don't pass it as the generic React `ref` prop to a plain
  // function component (which causes the "Function components cannot be given refs" warning).
  const { ref, ...fieldProps } = field;

  const internalOnBlur = field.onBlur;
  const propsOnBlur = selectFieldProps.onBlur;

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLButtonElement>) => {
      internalOnBlur();
      propsOnBlur?.(event);
    },
    [internalOnBlur, propsOnBlur],
  );

  const onChange = useCallback(
    (newValue: TValue | null) => {
      onValueChange?.(field.name, newValue, field.value);
      field.onChange(newValue);
    },
    [field, onValueChange],
  );

  useInvokeOnChangeOnRequiredStateChange(field, selectFieldProps.isRequiredAsteriskShown ?? false);

  return (
    <RegrelloFormFieldSelect
      {...fieldProps}
      {...selectFieldProps}
      {...getErrorProp(fieldState)}
      inputRef={selectFieldProps.inputRef}
      onBlur={handleBlur}
      onChange={onChange}
      onClearClick={() => {
        field.onChange(null);
      }}
      selectRef={mergeRefs(ref, selectFieldProps.selectRef)}
    />
  );
}

export const RegrelloControlledFormFieldSelect = React.memo(
  RegrelloControlledFormFieldSelectInternal,
) as typeof RegrelloControlledFormFieldSelectInternal;
